// 사이드바 안읽음 빨간 점(메신저 멘션+DM / 메일 INBOX) 단일 저장소(patch_mm_unread_dot_fast_clear.md rev.3).
//
// 점이 새로고침 전까지 남던 원인: 메신저(MM)는 같은 출처 iframe 이라 그 안에서 읽어도 부모 창에 focus 가
// 안 생기고, 사이드바는 마운트·45초·focus/visibilitychange 에만 다시 물었다. 게다가 임베드는 숨김으로 계속
// 마운트돼 사이드바가 여러 벌이고, 벌마다 따로 폴링했다(같은 초에 /api/dashboard/unread 10건).
// 그래서
//  ① 값과 폴러를 여기 한 벌로 모으고(사이드바는 구독만), 중복 호출은 합친다.
//  ② MM iframe 이 읽음 API 를 부르면(watchMmReadCalls) 곧바로 다시 묻는다.

export const UNREAD_POLL_MS = 45000;            // 기존 사이드바 폴링 주기 그대로
export const UNREAD_MIN_GAP_MS = 1000;          // 직전 완료 뒤 최소 간격(일반 호출)
export const UNREAD_REQUEST_TIMEOUT_MS = 10000; // 요청 하나의 상한(매달린 요청이 이후 갱신을 막지 않게)
export const MM_READ_REFRESH_DELAY_MS = 300;    // MM 읽음 API 응답 뒤 다시 묻기까지
export const MM_INPUT_REFRESH_DELAY_MS = 1500;  // MM 문서 pointerup·keyup 뒤(마지막 것 기준)
const WATCH_MARK = '__aigwMmReadWatch';         // iframe 창에 단다(재로드 = 새 창 = 다시 감싼다)
const DOC_MARK = '__aigwMmReadInput';           // iframe 문서에 단다(문서가 바뀌면 다시 건다)

const ZERO = Object.freeze({ messenger: 0, mail: 0 });

let unread = ZERO;
const listeners = new Set();
let inFlight = null;       // 진행 중 요청 Promise
let inFlightAbort = null;  // 그 요청의 중단 함수(resetUnread 용)
let lastDoneAt = null;     // 직전 요청 완료 시각(ms)
let forceAfter = false;    // 진행 중 요청이 끝난 뒤 1번 더(읽음 신호)
let deferTimer = null;     // 1초 창 끝에 1번(읽음 신호)
let gen = 0;               // resetUnread 마다 +1 — 앞 사용자 요청의 응답을 버린다
let pollRefs = 0;
let pollStop = null;
let readTimer = null;      // MM 읽음 API → 300ms
let inputTimer = null;     // MM pointerup·keyup → 1.5초

function now() {
  return Date.now();
}

function isHidden() {
  try {
    return typeof document !== 'undefined' && !!document.hidden;
  } catch (e) {
    return false;
  }
}

function readToken() {
  try {
    return localStorage.getItem('token');
  } catch (e) {
    return null;
  }
}

function notify() {
  listeners.forEach((fn) => {
    try { fn(unread); } catch (e) { /* 구독자 하나의 실패가 다른 사이드바를 깨지 않게 */ }
  });
}

// mail_checked_at = 메일 숫자를 센 시각(서버 UTC naive · 저장값일 때만). 응답에 있을 때만 키를 붙인다 —
//   없으면 키 자체가 없어 값 모양이 {messenger, mail} 그대로다(사이드바 «확인 시각» 툴팁 · #137 다 후속).
function setUnread(d) {
  const next = { messenger: d.messenger || 0, mail: d.mail || 0 };
  if (d.mail_checked_at) next.mail_checked_at = String(d.mail_checked_at);
  if (next.messenger === unread.messenger && next.mail === unread.mail
    && next.mail_checked_at === unread.mail_checked_at) return;
  unread = Object.freeze(next);
  notify();
}

// 설치형 앱 아이콘 배지 = 메신저 안읽음(메일은 섞지 않는다 · patch_mm_push_multidevice_n2_client.md §2-4).
// 🔴 값이 같아도 응답마다 다시 맞춘다 — 서비스워커가 알림을 띄우며 점을 찍거나 정리하며 지울 수 있어서다.
// 배지 미지원(navigator.setAppBadge 없음)·실패는 삼킨다.
function callBadge(name, arg) {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (!nav || typeof nav[name] !== 'function') return;
    const p = arg === undefined ? nav[name]() : nav[name](arg);
    if (p && typeof p.catch === 'function') p.catch(() => { /* */ });
  } catch (e) { /* */ }
}

function syncAppBadge(messenger) {
  if (messenger > 0) callBadge('setAppBadge', messenger);
  else callBadge('clearAppBadge');
}

export function getUnread() {
  return unread;
}

// 반환값 = 구독 해제 함수. 값이 바뀔 때만 fn(값) 을 부른다(같은 값이면 부르지 않는다).
export function subscribe(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function clearDeferred() {
  if (deferTimer !== null) {
    clearTimeout(deferTimer);
    deferTimer = null;
  }
}

function startRequest() {
  // 지금 출발하는 요청은 미뤄 둔 읽음 신호보다 뒤에 나가므로 그 신호를 채운다.
  clearDeferred();
  const myGen = gen;
  const token = readToken();   // 🔴 호출 시점에 읽는다(로그인·로그아웃 뒤 옛 토큰을 쓰지 않게)
  const API = process.env.REACT_APP_API_URL || '';
  let controller = null;
  try { controller = typeof AbortController === 'function' ? new AbortController() : null; } catch (e) { controller = null; }
  let abortTimer = null;
  let expired = false;
  let stopWait = () => {};
  const timedOut = new Promise((resolve) => {
    stopWait = () => resolve(null);
    abortTimer = setTimeout(() => {
      abortTimer = null;
      expired = true;
      try { if (controller) controller.abort(); } catch (e) { /* */ }
      resolve(null);   // fetch 가 중단 신호를 무시해도 여기서 끝낸다
    }, UNREAD_REQUEST_TIMEOUT_MS);
  });
  let req;
  try {
    const init = { headers: { Authorization: `Bearer ${token}` } };
    if (controller) init.signal = controller.signal;
    req = Promise.resolve(fetch(`${API}/api/dashboard/unread`, init))
      .then((r) => (r && r.ok ? r.json() : null));
  } catch (e) {
    req = Promise.reject(e);
  }
  const p = Promise.race([req, timedOut])
    .then((d) => {
      if (d && myGen === gen) {
        setUnread(d);
        syncAppBadge(unread.messenger);
      }
    })
    .catch(() => { /* 실패는 무시(다음 주기에 다시 묻는다) — 기존 사이드바와 같다 */ })
    .then(() => {
      if (abortTimer !== null) { clearTimeout(abortTimer); abortTimer = null; }
      if (inFlight === p) {
        inFlight = null;
        inFlightAbort = null;
        // 10초 상한으로 끊긴 요청은 값을 못 받았다 → 1초 창을 걸지 않는다(다음 호출이 바로 나간다).
        if (!expired) lastDoneAt = now();
        if (forceAfter) {
          // 읽음 신호가 이 요청 진행 중에 왔다 = 이 응답은 읽음 이전 값일 수 있다 → 1번 더.
          forceAfter = false;
          if (!isHidden()) startRequest();
        }
      }
      return unread;
    });
  inFlight = p;
  inFlightAbort = () => {
    if (abortTimer !== null) { clearTimeout(abortTimer); abortTimer = null; }
    try { if (controller) controller.abort(); } catch (e) { /* */ }
    stopWait();
  };
  return p;
}

// GET /api/dashboard/unread 로 값을 갱신한다. 숨은 탭에선 건너뛴다(부하감사 F3).
//  - 일반 호출: 진행 중이면 그 Promise 에 합치고, 직전 완료 1초 안이면 건너뛴다.
//  - force(읽음 신호): 버리지 않고 미룬다 — 진행 중이면 끝난 뒤 1번 더, 1초 창 안이면 창 끝에 1번.
//    미룬 호출이 여럿이어도 1번으로 합친다.
export function refreshUnread(opts) {
  const force = !!(opts && opts.force);
  if (isHidden()) return Promise.resolve(unread);
  if (inFlight) {
    if (force) forceAfter = true;
    return inFlight;
  }
  let wait = lastDoneAt === null ? 0 : (lastDoneAt + UNREAD_MIN_GAP_MS) - now();
  if (wait > UNREAD_MIN_GAP_MS) {   // 시계가 뒤로 갔다 — 창을 버린다(안 그러면 그만큼 갱신이 멈춘다)
    lastDoneAt = null;
    wait = 0;
  }
  if (wait > 0) {
    if (force && deferTimer === null) {
      deferTimer = setTimeout(() => {
        deferTimer = null;
        refreshUnread({ force: true });
      }, wait);
    }
    return Promise.resolve(unread);
  }
  return startRequest();
}

// 폴러 1벌(참조 계수). 첫 시작 때 즉시 1회 + 45초 interval + focus + visibilitychange(보일 때).
// 반환값 = 해제 함수. 마지막 해제 때 전부 지운다.
export function startUnreadPolling() {
  pollRefs += 1;
  if (pollRefs === 1) {
    const iv = setInterval(() => { refreshUnread(); }, UNREAD_POLL_MS);
    const onFocus = () => { refreshUnread(); };
    const onVis = () => { if (!isHidden()) refreshUnread(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    pollStop = () => {
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
    refreshUnread();
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pollRefs = Math.max(0, pollRefs - 1);
    if (pollRefs === 0 && pollStop) {
      const stop = pollStop;
      pollStop = null;
      stop();
    }
  };
}

// 값 {0,0} · 진행 중 · 직전 완료 시각 · 미룬 호출을 비운다(사용자 전환 — 다음 사용자에게 앞 사람 점이 안 보이게).
// 폴러 참조는 그대로 둔다(사이드바가 걸고 푼다).
export function resetUnread() {
  gen += 1;
  const abort = inFlightAbort;
  inFlight = null;
  inFlightAbort = null;
  if (abort) abort();
  lastDoneAt = null;
  forceAfter = false;
  clearDeferred();
  if (readTimer !== null) { clearTimeout(readTimer); readTimer = null; }
  if (inputTimer !== null) { clearTimeout(inputTimer); inputTimer = null; }
  callBadge('clearAppBadge');   // 다음 사용자에게 앞 사람 배지가 남지 않게
  if (unread.messenger !== 0 || unread.mail !== 0 || unread.mail_checked_at) {
    unread = ZERO;
    notify();
  }
}

// ── MM iframe 읽음 감지 ──────────────────────────────────────────────

function scheduleReadRefresh() {
  // 🔴 부모 창의 타이머 — iframe 이 다른 주소로 가도 살아 있다. 연달아 오면 마지막 응답 기준 1번.
  if (readTimer !== null) clearTimeout(readTimer);
  readTimer = window.setTimeout(() => {
    readTimer = null;
    refreshUnread({ force: true });
  }, MM_READ_REFRESH_DELAY_MS);
}

function scheduleInputRefresh() {
  if (inputTimer !== null) clearTimeout(inputTimer);
  inputTimer = window.setTimeout(() => {
    inputTimer = null;
    refreshUnread({ force: true });
  }, MM_INPUT_REFRESH_DELAY_MS);
}

function pathOf(url) {
  return String(url || '').split(/[?#]/)[0];
}

// GET 이 아닌 요청 중 경로에 읽음 경로가 들어간 것(실측: POST …/channels/members/me/view ·
// PUT …/threads/<id>/read/<ts>). 넓게 잡아 «모두 읽음» 같은 다른 읽음 경로도 덮는다.
export function isMmReadCall(method, url) {
  const m = String(method || 'GET').toUpperCase();
  if (m === 'GET') return false;
  const path = pathOf(url);
  return path.indexOf('/api/v4/channels/members/') !== -1 || path.indexOf('/threads/') !== -1;
}

function fetchRequestInfo(input, init) {
  let url = '';
  let method = '';
  if (typeof input === 'string') {
    url = input;
  } else if (input && typeof input.url === 'string') {   // Request
    url = input.url;
    method = input.method || '';
  } else if (input != null) {                             // URL 등
    url = String(input);
  }
  if (init && init.method) method = init.method;
  return { url, method: String(method || 'GET').toUpperCase() };
}

function wrapFetch(win) {
  const original = win.fetch;
  if (typeof original !== 'function') return false;
  const watched = function aigwMmReadFetch(input, init) {
    const res = original.apply(this, arguments);
    try {
      const info = fetchRequestInfo(input, init);
      if (isMmReadCall(info.method, info.url) && res && typeof res.then === 'function') {
        res.then((r) => { if (r && r.ok) scheduleReadRefresh(); }, () => {});
      }
    } catch (e) { /* 감시 실패 = 원래 fetch 그대로 */ }
    return res;
  };
  win.fetch = watched;
  return win.fetch === watched;
}

function wrapXhr(win) {
  const X = win.XMLHttpRequest;
  const proto = X && X.prototype;
  if (!proto || typeof proto.open !== 'function' || typeof proto.send !== 'function') return false;
  const originalOpen = proto.open;
  const originalSend = proto.send;
  proto.open = function aigwMmReadOpen(method, url) {
    try { this.__aigwMmReadReq = { method: String(method || 'GET').toUpperCase(), url: String(url || '') }; } catch (e) { /* */ }
    return originalOpen.apply(this, arguments);
  };
  proto.send = function aigwMmReadSend() {
    try {
      const req = this.__aigwMmReadReq;
      if (req && isMmReadCall(req.method, req.url) && typeof this.addEventListener === 'function') {
        const xhr = this;
        xhr.addEventListener('load', () => {
          try { if (xhr.status >= 200 && xhr.status < 300) scheduleReadRefresh(); } catch (e) { /* */ }
        });
      }
    } catch (e) { /* 감시 실패 = 원래 XHR 그대로 */ }
    return originalSend.apply(this, arguments);
  };
  return true;
}

function onMmInput() {
  scheduleInputRefresh();
}

// MM iframe 문서의 창(doc.defaultView)의 fetch·XHR 을 감싸 읽음 API 호출(ok 응답)을 보면 300ms 뒤 다시 묻는다.
// 같은 문서의 pointerup·keyup 도 1.5초 뒤(마지막 것 기준) 다시 묻는다 — MM 이 fetch 참조를 미리 잡아 둬
// 감싸기가 안 먹는 경우의 보강. 감싸기는 창마다 1회(표식), 입력 리스너는 문서마다 1회.
// 요청·응답 본문은 건드리지 않는다. 감싸기 실패는 삼킨다(원래 동작 그대로).
export function watchMmReadCalls(doc) {
  let win = null;
  try { win = doc && doc.defaultView; } catch (e) { win = null; }
  if (win) {
    try {
      if (!win[WATCH_MARK]) {
        let wrapped = false;
        try { if (wrapFetch(win)) wrapped = true; } catch (e) { /* */ }
        try { if (wrapXhr(win)) wrapped = true; } catch (e) { /* */ }
        if (wrapped) win[WATCH_MARK] = true;
      }
    } catch (e) { /* */ }
  }
  try {
    if (doc && !doc[DOC_MARK] && typeof doc.addEventListener === 'function') {
      doc.addEventListener('pointerup', onMmInput, true);
      doc.addEventListener('keyup', onMmInput, true);
      doc[DOC_MARK] = true;
    }
  } catch (e) { /* */ }
}
